import React, { useState } from 'react';
import type { KeyboardEventHandler } from 'react';
import type { FieldValues, Path, UseFormRegister } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { shouldAutoFill } from '../../../common/utils/autoFill';
import { EyeIcon, EyeSlashIcon } from '../icons';
import { BasicInputStyles } from './BasicInput';

const StyledIcon = styled.div(
  ({ theme }) => css`
    cursor: pointer;
    position: absolute;
    color: ${theme.colors.main.secondary};
    right: 0;
    margin-right: 0.75em;
    top: 0.75em;
  `
);

const StyledWrapper = styled.div(
  ({ theme }) => css`
    position: relative;
    width: 100%;
  `
);

type InputPasswordProps<Fields extends FieldValues> = {
  id?: string;
  label?: string;
  errorMessage?: string;
  required?: boolean;
  disabled?: boolean;
  inputSize?: string;
  value?: string | number;
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  name: Path<Fields>;
  register?: UseFormRegister<Fields>;
  autoFill?: boolean;
};

const Input = styled(BasicInputStyles)``;

const InputPassword = <Fields extends FieldValues>({
  id,
  errorMessage,
  required,
  disabled,
  inputSize = 'md',
  value,
  name,
  register,
  onKeyUp,
  label,
  autoFill = false,
  ariaLabel = label
}: InputPasswordProps<Fields>) => {
  const [reveal, setReveal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const registerFunc = register ? register : () => {};
  const fill = shouldAutoFill(autoFill);
  let lpIgnore = {};

  if (autoFill === false) {
    lpIgnore = { 'data-lpignore': true };
  }

  return (
    <StyledWrapper>
      <Input
        id={id ? `${id}-input-password` : undefined}
        type={reveal ? 'text' : 'password'}
        value={value}
        inputSize={inputSize}
        required={required}
        disabled={disabled}
        errorMessage={errorMessage}
        onKeyUp={onKeyUp}
        {...registerFunc(name)}
        alt={label}
        autoComplete={fill}
        // last pass autocomplete
        {...lpIgnore}
        aria-label={ariaLabel}
      />
      {reveal ? (
        <StyledIcon
          title="Hide password"
          aria-label={`${ariaLabel} Hide`}
          onClick={() => setReveal(false)}
        >
          <EyeSlashIcon size="1.25em" />
        </StyledIcon>
      ) : (
        <StyledIcon
          title="Show password"
          aria-label={`${ariaLabel} Show`}
          onClick={() => setReveal(true)}
        >
          <EyeIcon size="1.25em" />
        </StyledIcon>
      )}
    </StyledWrapper>
  );
};

export { InputPassword };
